import React, { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, FileText } from "lucide-react";
import { LOAN_PRODUCTS, api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";

export default function LoanDetail() {
  const { slug } = useParams();
  const product = useMemo(() => LOAN_PRODUCTS.find((p) => p.slug === slug), [slug]);

  const { data: config } = useQuery({
    queryKey: ["doc-config-public", product?.key],
    queryFn: async () => {
      if (!product) return null;
      try {
        const { data } = await api.get(`/public/doc-config/${product.key}`);
        return data;
      } catch { return null; }
    },
    enabled: !!product,
  });

  if (!product) return <Navigate to="/services" replace />;

  return (
    <div>
      <section className="bg-[#06152D] text-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/services" className="text-sm text-[#B5C4E1] hover:text-white">← All services</Link>
          <div className="mt-4 flex items-baseline gap-4 flex-wrap">
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{product.label}</h1>
            <span className="bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-semibold text-[#8AB4FF]">As per Bank/NBFC Eligibility</span>
          </div>
          <p className="mt-4 text-lg text-[#B5C4E1] max-w-2xl">{product.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/apply?loan=${product.key}`}
              className="bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2"
              data-testid="loan-apply-cta"
            >
              Apply for this loan <ArrowRight size={16} />
            </Link>
            <Link to="/emi-calculator" className="bg-white/10 hover:bg-white/20 border border-white/25 text-white font-semibold px-6 py-3 rounded-full backdrop-blur">
              Calculate EMI
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold text-[#06152D]">Key details</h2>
            <dl className="mt-6 grid grid-cols-2 gap-6">
              {[
                ["Interest rate", "As per Bank/NBFC Eligibility"],
                ["Tenure", product.tenure],
                ["Min amount", "₹" + product.min.toLocaleString("en-IN")],
                ["Max amount", "₹" + product.max.toLocaleString("en-IN")],
              ].map(([k, v]) => (
                <div key={k} className="border-l-2 border-[#0052FF] pl-4">
                  <dt className="text-xs font-semibold tracking-[0.15em] uppercase text-[#52525B]">{k}</dt>
                  <dd className="mt-1 font-display text-xl font-bold text-[#06152D]">{v}</dd>
                </div>
              ))}
            </dl>

            <h3 className="mt-12 font-display text-xl font-bold text-[#06152D]">Documents required</h3>
            <p className="mt-1 text-sm text-[#52525B]">Loan Junction verifies and submits these to our partner bank/NBFC.</p>
            <ul className="mt-4 space-y-2">
              {(config?.requirements || []).map((r) => (
                <li key={r.key} className="flex items-start gap-2">
                  <FileText size={16} className="text-[#0052FF] mt-1 shrink-0" />
                  <span className="text-[#09090b]">{r.label}</span>
                  {r.required && <span className="text-xs text-[#DC2626] ml-auto">Required</span>}
                </li>
              ))}
              {(!config || config.requirements?.length === 0) && (
                <li className="text-sm text-[#52525B]">Standard KYC + income proof + purpose-specific documents. Our advisor will share a personalised checklist.</li>
              )}
            </ul>

            <h3 className="mt-12 font-display text-xl font-bold text-[#06152D]">How Loan Junction helps</h3>
            <ul className="mt-4 space-y-3">
              {[
                "One-time document collection — we share with all suitable partner banks/NBFCs.",
                "Application submitted to lenders on your behalf; we track status daily.",
                "Sanction letter, disbursement follow-up, and post-disbursal support.",
                "Zero customer fees — we are paid by our partner banks.",
              ].map((v) => (
                <li key={v} className="flex gap-2 items-start">
                  <CheckCircle2 size={16} className="text-[#059669] mt-0.5 shrink-0" />
                  <span className="text-[#09090b]">{v}</span>
                </li>
              ))}
            </ul>

            <p className="mt-8 text-xs text-[#52525B] italic">
              Loan Junction Finance Services is a loan sourcing consultancy (DSA). Final loan approval,
              rate, and disbursal decisions rest with the partner Bank / NBFC.
            </p>
          </div>

          <aside className="bg-[#F4F4F5] border border-[#E4E4E7] rounded-2xl p-6 h-fit sticky top-24">
            <div className="text-xs font-semibold tracking-[0.15em] uppercase text-[#0052FF]">Get started</div>
            <div className="mt-2 font-display text-xl font-bold text-[#06152D]">Talk to a Loan Junction advisor</div>
            <p className="mt-2 text-sm text-[#52525B]">Free consultation. No obligation.</p>
            <Link to={`/apply?loan=${product.key}`} className="mt-4 block text-center bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-3 rounded-full">
              Apply now
            </Link>
            <Link to="/contact" className="mt-2 block text-center border border-[#E4E4E7] text-[#06152D] font-semibold px-4 py-3 rounded-full hover:bg-white">
              Contact us
            </Link>
          </aside>
        </div>
      </section>
    </div>
  );
}
